// REST API クライアント。
// 認証モードに応じた ApiRequest を注入し、required 環境では Bearer トークン付きで呼び出す。
// JSON 不一致など本来発生しないケースは throw して TanStack Query 側で扱う。

import { useMemo } from "react";
import type { CanvasObject, CanvasPurgeStatus, CanvasSizePreset } from "@refinement-canvas/domain";
import { useApiRequest, type ApiRequest } from "../auth/api-request-context";

/**
 * 新規キャンバスの初回の名前入力の表示指示。サーバが「この利用者へ表示する」と判定したときだけ届く
 * （作成者本人・書き込み可・まだ一度も図形を置いていない・他の命名が先行していない）。
 * generation は確定の要求（resolveInitialCanvasName）へそのまま返す。
 */
export interface CanvasInitialNamingPrompt {
  readonly status: "pending";
  readonly generation: string;
}

/** 初回の名前入力の確定方法。「決定」は名前を保存し、「あとで決める」は既定名のまま解決する。 */
export type InitialCanvasNameRequest =
  | { readonly decision: "later"; readonly generation: string }
  | { readonly decision: "name"; readonly generation: string; readonly title: string };

/** POST /api/canvases/:canvasId/initial-name の応答。 */
export interface InitialCanvasNameResponse {
  /** 今回の要求で確定したか。false は別のタブ・他の参加者の命名や図形の配置が先行した等で、上書きしていない。 */
  readonly applied: boolean;
  /** 確定後（または確定できなかった時点）の最新のメタ。 */
  readonly meta: {
    readonly title: string;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly thumbnailKey?: string | undefined;
    readonly thumbnailUrl?: string | undefined;
    readonly exportKey?: string | undefined;
  };
  /** 同じ初回状態がまだ有効なとき（確定できなかったが再試行できる）だけ付く。 */
  readonly initialNaming?: CanvasInitialNamingPrompt | undefined;
}

interface BootstrapResponse {
  canvasId: string;
  serverSeq: number;
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
    /** キャンバスサイズのプリセット（小 / 中 / 大）。未設定はクライアントで既定=中(10K)として扱う。 */
    sizePreset?: CanvasSizePreset | undefined;
    /** @internal 参考情報のみ（META の素通し）。実体ありかつ非空かは export-url が最終判定する。UI 指標に使わない。 */
    exportKey?: string | undefined;
    isFavorite?: boolean | undefined;
    /** ゴミ箱にあり、参照専用として扱うか。旧サーバ互換のため optional。 */
    isTrashed?: boolean | undefined;
    /** 初回の名前入力の表示指示（表示するときだけ届く）。旧サーバ互換のため optional。 */
    initialNaming?: CanvasInitialNamingPrompt | undefined;
  };
  objects: readonly CanvasObject[];
  /**
   * 取り直し（options.resync）で、キャンバスがまだ作成されていないときだけ true（サーバは作成しない）。
   * メタと図形一覧は未作成のキャンバスの既定値のため、受け取った側は適用せず状態を変えない。
   */
  notCreated?: boolean | undefined;
}

export interface RecentCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  lastOpenedAt: string;
}

export interface FavoriteCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
  favoritedAt: string;
}

export interface AllCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
}

export interface TrashedCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  trashedAt: string;
  /**
   * ゴミ箱へ移した 1 回ごとの識別値（世代）。完全削除の要求（expectedTrashGeneration）へそのまま渡し、
   * 戻して入れ直した後の古い画面の要求を、今のゴミ箱状態と取り違えないようにする。旧サーバ互換のため optional。
   */
  trashGeneration?: string | undefined;
  /** 完全削除の処理中なら purging（TOP の再読み込みでも「削除処理中」を表示し続ける）。 */
  purgeState?: "purging" | undefined;
  /** 完全削除の処理中に失敗し、再試行を待っているか。 */
  purgeRetrying?: boolean | undefined;
}

/**
 * PUT /api/canvases/:canvasId/trash の応答。ゴミ箱へ移した日時と世代（旧サーバは返さない）。移したばかりの
 * カードから完全削除を始められるよう、楽観表示のカードへそのまま入れる。
 */
export interface CanvasTrashResponse {
  readonly trashedAt?: string | undefined;
  readonly trashGeneration?: string | undefined;
}

/** POST /api/canvases/:canvasId/purge の要求。 */
export interface CanvasPurgeRequest {
  /** 確認入力の文字列（「完全に削除」と完全一致したときだけサーバが受け付ける）。 */
  readonly confirmation: string;
  /** 画面が見ていたゴミ箱状態の世代。 */
  readonly expectedTrashGeneration: string;
}

/** POST /api/canvases/:canvasId/purge の応答（202。同じ世代の処理が既にあれば、その状態）。 */
export interface CanvasPurgeStartResponse {
  readonly state: "purging" | "purged";
  readonly jobId: string;
}

/** GET /api/canvases/:canvasId/purge-status の応答。none は完全削除が始まっていない。 */
export interface CanvasPurgeStatusResponse {
  readonly state: "none" | CanvasPurgeStatus;
  readonly jobId?: string | undefined;
}

/** GET /api/me のレスポンス（権限フラグを含むアクター情報）。 */
export interface MeResponse {
  actor: {
    userId: string;
    displayName: string;
    permissions: {
      canRead: boolean;
      canWrite: boolean;
      canAdmin: boolean;
    };
  };
}

export interface OgFetchResult {
  url: string;
  title?: string | undefined;
  description?: string | undefined;
  image?: string | undefined;
  siteName?: string | undefined;
}

export interface RealtimeSessionTicket {
  token: string;
  expiresAt: string;
}

interface PresignedUpload {
  uploadUrl: string;
  bucket: string;
  key: string;
  expiresInSeconds: number;
}

/**
 * 全体像 PDF の presign 応答。PUT 先は配信キーではなく staging 一意キー
 * （exports-staging/<canvasId>/<uploadId>.pdf）で、finalize がこの uploadId で staging 実体を照合し、検証を
 * 通ったものだけ配信キーへ昇格する。クライアントは PUT 時に x-amz-meta-export-upload-id ヘッダーへ同じ値を載せ、
 * finalize へこの uploadId を渡す。
 */
interface ExportPresignedUpload extends PresignedUpload {
  uploadId: string;
}

interface UpdateCanvasMetaResponse {
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
    sizePreset?: CanvasSizePreset | undefined;
    /** @internal 参考情報のみ（META の素通し）。実体ありかつ非空かは export-url が最終判定する。UI 指標に使わない。 */
    exportKey?: string | undefined;
  };
}

interface FinalizeExportResponse {
  // 今回のアップロードを配信キーの current として昇格し exportUpdatedAt を確定したか。
  // false は世代ゲート（描画後に他者の drawable 編集が確定）や昇格後の再確認で stale と判定され、昇格しなかった
  // または昇格後に版を取り消した等で確定できなかったことを示す。クライアントは dirty を保持して新しい uploadId で
  // 再保存し、最新の全体像へ収束させる（確定時刻の停滞を残さない）。
  confirmed: boolean;
  exportKey?: string | undefined;
  updatedAt: string;
}

export interface CanvasApiClient {
  /**
   * キャンバスのスナップショットを取得する。未作成キャンバスはサーバ側で lazy create される。
   * createName を渡すと、その lazy create 時の初期キャンバス名として使われる（既存キャンバスでは無視）。
   * options.nameProvided は、画面の URL に name キーがある（値が空・空白でも）ことをサーバへ伝え、
   * lazy create で初回の名前入力を作らせないために使う。options.resync は、画面の最初の取得ではない
   * 取り直し（再接続・再同期・確定の確認）であることを伝え、未作成のキャンバスを作成させない
   * （URL の名前指定や最初の取得の成否を知らない取得のため。未作成なら notCreated が true の応答になる）。
   * options.signal で要求を打ち切れる。
   */
  readonly fetchCanvasBootstrap: (
    canvasId: string,
    createName?: string,
    options?: {
      readonly nameProvided?: boolean | undefined;
      readonly resync?: boolean | undefined;
      readonly signal?: AbortSignal | undefined;
    },
  ) => Promise<BootstrapResponse>;
  /**
   * 新規キャンバスの初回の名前入力を確定する。非 2xx は CanvasApiHttpError（status 付き）で失敗させ、
   * 呼び出し側が「確定済みかもしれない失敗（通信断・5xx）」と「確定していない失敗（4xx）」を区別できるようにする。
   */
  readonly resolveInitialCanvasName: (
    canvasId: string,
    request: InitialCanvasNameRequest,
    options?: { readonly signal?: AbortSignal | undefined },
  ) => Promise<InitialCanvasNameResponse>;
  readonly fetchRecentCanvases: () => Promise<{ items: readonly RecentCanvasItem[] }>;
  readonly deleteRecentCanvas: (canvasId: string) => Promise<void>;
  /** 現在ログインしているユーザーの権限情報を取得する（管理者判定に使う）。 */
  readonly fetchMe: () => Promise<MeResponse>;
  /** ログインユーザーのお気に入りキャンバス一覧（登録日時の新しい順）。 */
  readonly fetchFavorites: () => Promise<{ items: readonly FavoriteCanvasItem[] }>;
  /** 現在のキャンバスをお気に入り登録する。 */
  readonly addFavorite: (canvasId: string) => Promise<void>;
  /** 現在のキャンバスのお気に入りを解除する。 */
  readonly removeFavorite: (canvasId: string) => Promise<void>;
  /** 管理者専用: 全キャンバス一覧（更新日時の新しい順・最大 48 件）。 */
  readonly fetchAllCanvases: () => Promise<{ items: readonly AllCanvasItem[] }>;
  /** 現在の利用者から見えるゴミ箱キャンバス一覧。 */
  readonly fetchTrash: () => Promise<{ items: readonly TrashedCanvasItem[] }>;
  /** キャンバスを可逆なゴミ箱状態へ移す（応答はゴミ箱へ移した日時と世代）。 */
  readonly trashCanvas: (canvasId: string) => Promise<CanvasTrashResponse>;
  /** キャンバスをゴミ箱から戻す。 */
  readonly restoreCanvas: (canvasId: string) => Promise<void>;
  /**
   * ゴミ箱内のキャンバスの完全削除を始める。非 2xx は CanvasApiHttpError（status とサーバの文言付き）で
   * 失敗させ、呼び出し側が「始まっていない失敗（4xx）」と「始まったかもしれない失敗（通信断・5xx）」を区別できる
   * ようにする。
   */
  readonly purgeCanvas: (
    canvasId: string,
    request: CanvasPurgeRequest
  ) => Promise<CanvasPurgeStartResponse>;
  /** 完全削除の状態を取得する（処理中・再試行待ち・済み。始まっていなければ none）。 */
  readonly fetchPurgeStatus: (canvasId: string) => Promise<CanvasPurgeStatusResponse>;
  readonly updateCanvasMeta: (
    canvasId: string,
    input: {
      title?: string | undefined;
      thumbnailKey?: string | null | undefined;
    }
  ) => Promise<UpdateCanvasMetaResponse>;
  /**
   * 全体像 PDF の presigned PUT 完了後に呼び、サーバが S3 実体を HeadObject で確認してから exportKey を確定する。
   * exportKey は汎用メタ更新では立てられないため、確定はこの経路に限定する（実体の無い exportKey を防ぐ）。
   */
  readonly finalizeExportUpload: (params: {
    canvasId: string;
    uploadId: string;
    // PDF を描画した時点のサーバ確定世代。サーバはこれを現在の最新世代と比較し、描画後に drawable 変更が確定して
    // いれば（同時編集の遅延受信を含む）confirmed=false を返し、最新状態での再保存を促す。
    serverSeq: number;
  }) => Promise<FinalizeExportResponse>;
  readonly createCanvasObject: (
    canvasId: string,
    object: CanvasObject
  ) => Promise<{ object: CanvasObject }>;
  readonly patchCanvasObject: (
    canvasId: string,
    objectId: string,
    input: PatchObjectInput
  ) => Promise<{ object: CanvasObject }>;
  readonly deleteCanvasObject: (canvasId: string, objectId: string) => Promise<void>;
  readonly presignImageUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignVideoUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignThumbnailUpload: (params: {
    canvasId: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignExportUpload: (params: {
    canvasId: string;
    contentLength: number;
  }) => Promise<ExportPresignedUpload>;
  readonly notifyUploadComplete: (
    canvasId: string,
    bucket: string,
    key: string
  ) => Promise<UploadCompleteResult>;
  /**
   * PDF 出力範囲の外へ初めて図形を置いたときの警告について、この利用者・このキャンバスでの表示権を確定する。
   * 勝者（最初の要求、または同じ attemptId の再送）だけが `{ show: true }` を受け取る。
   * 非 2xx は CanvasApiHttpError（status 付き）で失敗させ、呼び出し側が再試行の要否を判断できるようにする。
   */
  readonly claimPdfOutsideNotice: (
    canvasId: string,
    attemptId: string
  ) => Promise<{ readonly show: boolean }>;
  readonly fetchOgp: (url: string) => Promise<OgFetchResult>;
  /**
   * OGP リンクカードの画像を、同一オリジンの中継（GET /api/og/image）経由で取得する。
   * 相手サイトの Cross-Origin-Resource-Policy / CORS ヘッダーに依存せず読めるようにするための経路。
   */
  readonly fetchOgpImage: (imageUrl: string) => Promise<Blob>;
  readonly issueRealtimeSession: (canvasId: string) => Promise<RealtimeSessionTicket>;
}

/**
 * API がエラー応答（非 2xx）を返したことを表すエラー。
 * 呼び出し側が status を見て「恒久的な失敗（再取得しても同じ）」と「回復し得る失敗（認証失効など）」を
 * 区別できるようにするために持つ。serverMessage は応答本文の `{ "error": "<文言>" }` の文言（利用者向けの
 * 説明。本文がこの形でなければ undefined）。
 */
export class CanvasApiHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly serverMessage?: string | undefined
  ) {
    super(message);
    this.name = "CanvasApiHttpError";
  }
}

/** 応答本文の `{ "error": "<文言>" }` から文言を取り出す（形が違えば undefined）。 */
function parseServerMessage(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { readonly error?: unknown } | null;
    const message = parsed?.error;
    return typeof message === "string" && message.length > 0 ? message : undefined;
  } catch {
    return undefined;
  }
}

/** 非 2xx の応答を、status とサーバの文言を持つ CanvasApiHttpError にする。 */
async function toHttpError(response: Response): Promise<CanvasApiHttpError> {
  const body = await response.text().catch(() => "");
  return new CanvasApiHttpError(
    response.status,
    `API ${response.status}: ${body || response.statusText}`,
    parseServerMessage(body)
  );
}

async function callJson<T>(request: ApiRequest, path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init);
  if (!response.ok) {
    throw await toHttpError(response);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** バイナリ応答（画像など）を Blob として受け取る。JSON 前提の callJson は使えないため分ける。 */
async function callBlob(request: ApiRequest, path: string): Promise<Blob> {
  const response = await request(path);
  if (!response.ok) {
    throw await toHttpError(response);
  }
  return await response.blob();
}

const PURGE_STATUS_STATES: readonly CanvasPurgeStatusResponse["state"][] = [
  "none",
  "purging",
  "retrying",
  "purged",
];

// ---- canvas ----

export interface PatchObjectInput {
  transform?:
    | {
        x?: number | undefined;
        y?: number | undefined;
        w?: number | undefined;
        h?: number | undefined;
        rotation?: number | undefined;
        zOrder?: number | undefined;
      }
    | undefined;
  text?: string | undefined;
}

export interface UploadCompleteResult {
  ok: boolean;
  /** CloudFront 経由の公開 URL。未設定（ATTACHMENTS_PUBLIC_BASE_URL 未指定）の場合は null。 */
  publicUrl: string | null;
}

/**
 * 初回の名前入力の表示指示を検査し、形の崩れた値は「表示しない」として取り除く。
 * 確定の要求に使う generation が欠けた指示で入力を出すと、どの要求も確定できず閉じられなくなるため。
 */
function sanitizeInitialNamingPrompt(value: unknown): CanvasInitialNamingPrompt | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const record = value as { readonly status?: unknown; readonly generation?: unknown };
  if (record.status !== "pending") return undefined;
  if (typeof record.generation !== "string" || record.generation.length === 0) return undefined;
  return { status: "pending", generation: record.generation };
}

export function createCanvasApiClient(request: ApiRequest): CanvasApiClient {
  return {
    async fetchCanvasBootstrap(canvasId, createName, options) {
      // 新規作成時の初期名を引き継ぐため、指定があれば ?name= を付与する（サーバは lazy create 時のみ採用）。
      // 画面の URL に name キーがあるだけ（値が空・空白）のときも、nameProvided=1 で「名前の指定あり」を伝える。
      const params: string[] = [];
      if (createName !== undefined && createName.length > 0) {
        params.push(`name=${encodeURIComponent(createName)}`);
      }
      if (options?.nameProvided === true) {
        params.push("nameProvided=1");
      }
      // 画面の最初の取得ではない取り直しは、resync=1 で未作成のキャンバスを作成させない。
      if (options?.resync === true) {
        params.push("resync=1");
      }
      const query = params.length > 0 ? `?${params.join("&")}` : "";
      const data = await callJson<BootstrapResponse>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}${query}`,
        options?.signal !== undefined ? { signal: options.signal } : undefined,
      );
      const { initialNaming, ...meta } = data.meta;
      const prompt = sanitizeInitialNamingPrompt(initialNaming);
      return { ...data, meta: prompt !== undefined ? { ...meta, initialNaming: prompt } : meta };
    },
    async resolveInitialCanvasName(canvasId, body, options) {
      const response = await request(`/canvases/${encodeURIComponent(canvasId)}/initial-name`, {
        method: "POST",
        body: JSON.stringify(body),
        ...(options?.signal !== undefined ? { signal: options.signal } : {}),
      });
      if (!response.ok) {
        throw await toHttpError(response);
      }
      const { initialNaming, ...rest } = (await response.json()) as InitialCanvasNameResponse;
      const prompt = sanitizeInitialNamingPrompt(initialNaming);
      return prompt !== undefined ? { ...rest, initialNaming: prompt } : rest;
    },
    fetchRecentCanvases() {
      return callJson<{ items: readonly RecentCanvasItem[] }>(request, "/me/recent-canvases");
    },
    deleteRecentCanvas(canvasId) {
      return callJson<void>(request, `/me/recent-canvases/${encodeURIComponent(canvasId)}`, {
        method: "DELETE",
      });
    },
    fetchMe() {
      return callJson<MeResponse>(request, "/me");
    },
    fetchFavorites() {
      return callJson<{ items: readonly FavoriteCanvasItem[] }>(request, "/me/favorites");
    },
    addFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "PUT",
      });
    },
    removeFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "DELETE",
      });
    },
    fetchAllCanvases() {
      return callJson<{ items: readonly AllCanvasItem[] }>(request, "/admin/canvases");
    },
    fetchTrash() {
      return callJson<{ items: readonly TrashedCanvasItem[] }>(request, "/me/trash");
    },
    async trashCanvas(canvasId) {
      const data = await callJson<{
        readonly trashedAt?: unknown;
        readonly trashGeneration?: unknown;
      } | null>(request, `/canvases/${encodeURIComponent(canvasId)}/trash`, {
        method: "PUT",
      });
      // 形の違う値（旧サーバ・想定外の応答）は使わない（世代の無いカードは一覧を取り直すまで完全削除を選べない）。
      const trashedAt = typeof data?.trashedAt === "string" ? data.trashedAt : undefined;
      const trashGeneration =
        typeof data?.trashGeneration === "string" && data.trashGeneration.length > 0
          ? data.trashGeneration
          : undefined;
      return {
        ...(trashedAt !== undefined ? { trashedAt } : {}),
        ...(trashGeneration !== undefined ? { trashGeneration } : {}),
      };
    },
    restoreCanvas(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/trash`, {
        method: "DELETE",
      });
    },
    async purgeCanvas(canvasId, body) {
      const data = await callJson<{ readonly state?: unknown; readonly jobId?: unknown }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/purge`,
        {
          method: "POST",
          body: JSON.stringify({
            confirmation: body.confirmation,
            expectedTrashGeneration: body.expectedTrashGeneration,
          }),
        }
      );
      if ((data.state !== "purging" && data.state !== "purged") || typeof data.jobId !== "string") {
        throw new Error("完全削除の開始の応答の形が不正です。");
      }
      return { state: data.state, jobId: data.jobId };
    },
    async fetchPurgeStatus(canvasId) {
      const data = await callJson<{ readonly state?: unknown; readonly jobId?: unknown }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/purge-status`
      );
      const state = PURGE_STATUS_STATES.find((candidate) => candidate === data.state);
      if (state === undefined) {
        throw new Error("完全削除の状態の応答の形が不正です。");
      }
      return typeof data.jobId === "string" ? { state, jobId: data.jobId } : { state };
    },
    updateCanvasMeta(canvasId, input) {
      return callJson<UpdateCanvasMetaResponse>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}`,
        { method: "PUT", body: JSON.stringify(input) }
      );
    },
    createCanvasObject(canvasId, object) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects`,
        { method: "POST", body: JSON.stringify({ object }) }
      );
    },
    patchCanvasObject(canvasId, objectId, input) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "PATCH", body: JSON.stringify(input) }
      );
    },
    deleteCanvasObject(canvasId, objectId) {
      return callJson<void>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "DELETE" }
      );
    },
    presignImageUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:image`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignVideoUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:video`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignThumbnailUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/thumbnail`,
        {
          method: "POST",
          body: JSON.stringify({ contentLength: params.contentLength }),
        }
      );
    },
    presignExportUpload(params) {
      return callJson<ExportPresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/export`,
        {
          method: "POST",
          body: JSON.stringify({ contentLength: params.contentLength }),
        }
      );
    },
    finalizeExportUpload(params) {
      return callJson<FinalizeExportResponse>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/export/finalize`,
        {
          method: "POST",
          body: JSON.stringify({ uploadId: params.uploadId, serverSeq: params.serverSeq }),
        }
      );
    },
    notifyUploadComplete(canvasId, bucket, key) {
      return callJson<UploadCompleteResult>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/uploads:complete`,
        {
          method: "POST",
          body: JSON.stringify({ bucket, key }),
        }
      );
    },
    async claimPdfOutsideNotice(canvasId, attemptId) {
      const response = await request(
        `/canvases/${encodeURIComponent(canvasId)}/notices/pdf-outside/claim`,
        { method: "POST", body: JSON.stringify({ attemptId }) }
      );
      if (!response.ok) {
        throw await toHttpError(response);
      }
      const data = (await response.json()) as { readonly show?: unknown };
      return { show: data.show === true };
    },
    fetchOgp(url) {
      return callJson<OgFetchResult>(request, `/og?url=${encodeURIComponent(url)}`);
    },
    fetchOgpImage(imageUrl) {
      return callBlob(request, `/og/image?url=${encodeURIComponent(imageUrl)}`);
    },
    issueRealtimeSession(canvasId) {
      return callJson<RealtimeSessionTicket>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/realtime-session`
      );
    },
  };
}

export function useCanvasApi(): CanvasApiClient {
  const request = useApiRequest();
  return useMemo(() => createCanvasApiClient(request), [request]);
}
